import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router'
import * as R from 'remeda'

import { graphql } from '~/gql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { applyIfExist, filterExists } from '~/utils'

import { useSurveyForSettingsQueryKey } from './useSurveyForSettingsQueryKey'

const document = graphql(`
  query UseSurveyForSettingsQuery_Main($surveyId: ID!) {
    survey(id: $surveyId, questionDisplayFilter: SEGMENT_DEFINITIONS) {
      id
      title
      brandAccountId
      questionnaireFileUploadedAt
      answerFileUploadedAt
      brandGroups {
        id
        name
        mappingInProgress
        mappedAt
        brands {
          id
          name
          number
        }
        segment_definitions {
          segment_number
          condition_groups {
            question_id
            operation
            use_brand_number_as_answer
            use_brand_number_as_question_number
            values
            text_values
          }
        }
      }
      questions {
        question_id
        question_text
        answer_options {
          option
          text
        }
        mt_questions
        answer_type
      }
      metadata {
        surveyStartDate {
          year
          month
          day
        }
        tamName
        tamNumber
        billingTypeName
        purchaseDecisionFrequencyName
        isFreeName
        definitionTypeName
        purchaseMainChannelName
        isUseViaSales
        industryName
      }
    }
  }
`)

export const useSurveyForSettingsQuery = () => {
  const { surveyId = '' } = useParams<{ surveyId: string | undefined }>()

  const graphQLRequest = useGraphQLRequest()

  const queryKey = useSurveyForSettingsQueryKey()

  return useQuery({
    queryFn: async () => {
      const result = await graphQLRequest({ document, variables: { surveyId } })

      return R.pipe(
        result.survey,
        applyIfExist((survey) => ({
          ...survey,
          questionnaireFileUploadedAt:
            survey.questionnaireFileUploadedAt ?? undefined,
          answerFileUploadedAt: survey.answerFileUploadedAt ?? undefined,
          brandGroups: R.pipe(
            survey.brandGroups,
            filterExists,
            R.map((brandGroup) => ({
              ...brandGroup,
              mappingInProgress: brandGroup.mappingInProgress ?? undefined,
              mappedAt: brandGroup.mappedAt ?? undefined,
              brands: R.pipe(brandGroup.brands, filterExists),
              segment_definitions: R.pipe(
                brandGroup.segment_definitions,
                filterExists,
                R.map((segmentDefinition) => ({
                  ...segmentDefinition,
                  condition_groups: R.pipe(
                    segmentDefinition.condition_groups,
                    filterExists,
                    R.map((conditionGroup) =>
                      R.pipe(
                        conditionGroup,
                        filterExists,
                        R.map((condition) => ({
                          ...condition,
                          use_brand_number_as_answer:
                            condition.use_brand_number_as_answer ?? false,
                          use_brand_number_as_question_number:
                            condition.use_brand_number_as_question_number ??
                            false,
                          values: R.pipe(condition.values, filterExists),
                          text_values: R.pipe(
                            condition.text_values,
                            filterExists,
                          ),
                        })),
                      ),
                    ),
                  ),
                })),
              ),
            })),
          ),
          questions: R.pipe(survey.questions, filterExists),
          metadata: R.pipe(
            survey.metadata,
            applyIfExist((metadata) => ({
              ...metadata,
              surveyStartDate: metadata.surveyStartDate ?? undefined,
              tamName: metadata.tamName ?? undefined,
              tamNumber: metadata.tamNumber ?? undefined,
              billingTypeName: metadata.billingTypeName ?? undefined,
              purchaseDecisionFrequencyName:
                metadata.purchaseDecisionFrequencyName ?? undefined,
              isFreeName: metadata.isFreeName ?? undefined,
              definitionTypeName: metadata.definitionTypeName ?? undefined,
              purchaseMainChannelName:
                metadata.purchaseMainChannelName ?? undefined,
              isUseViaSales: metadata.isUseViaSales ?? undefined,
              industryName: metadata.industryName ?? undefined,
            })),
          ),
        })),
      )
    },
    queryKey,
  })
}

export type Survey = NonNullable<
  QueryResultOf<typeof useSurveyForSettingsQuery>
>
export type BrandGroup = Survey['brandGroups'][number]
export type Brand = BrandGroup['brands'][number]
export type Question = Survey['questions'][number]
